// GoogleSpeechToText.js - Advanced speech recognition with Google Cloud Speech-to-Text API
// Supports automatic language detection for English, Chinese, and Malay
// With fallback support for Android WebView environments

import { useState, useEffect, useCallback } from 'react';

// Supported languages for the chatbot speech recognition
const SUPPORTED_LANGUAGES = {
  en: { name: 'English', code: 'en-US' },
  zh: { name: '中文 (Chinese)', code: 'zh-CN' }, // Mandarin for recognition
  ms: { name: 'Bahasa Melayu (Malay)', code: 'ms-MY' },
  ur: { name: 'اردو (Urdu)', code: 'ur-PK' }
};

// Detect if running in Android WebView
const isAndroidWebView = () => {
  const userAgent = navigator.userAgent || '';
  return /\bwv\b/.test(userAgent) && /Android/.test(userAgent);
};

// List of local place names for post-processing and suggestions
export const LOCAL_PLACE_NAMES = [
  'Petaling Jaya', 'KL Sentral', 'Kuala Lumpur', 'Subang', 'Seremban', 'Ipoh', 'Melaka', 'Shah Alam', 'Kajang',
  'Bukit Bintang', 'Cheras', 'Kepong', 'Sentul', 'Titiwangsa', 'Wangsa Maju', 'Batu Caves', 'Taman Desa', 'Bandar Baru',
  'Seri Kembangan', 'Balakong', 'Semenyih', 'Semarak', 'Kerinchi', 'Pantai Dalam', 'Mont Kiara', 'Ampang', 'Rawang',
  'Gombak', 'Klang', 'Nilai', 'Bangi', 'Setiawangsa', 'Puchong', 'Kinrara', 'Sunway', 'Damansara', 'Tropicana',
  'USJ', 'Putrajaya', 'Cyberjaya', 'Langkawi', 'Pulau Pinang', 'Penang', 'Sri Hartamas', 'Desa ParkCity', 'Kajang',
  'Selayang', 'Melawati', 'Wangsa Maju', 'Sri Kembangan', 'Bukit Jalil', 'Sri Petaling', 'Pantai Hillpark', 'Sungai Buloh',
  'Glenmarie', 'Seksyen 13', 'Bandar Utama', 'TTDI', 'Damansara Utama', 'Damansara Jaya', 'Kota Damansara', 'Ara Damansara'
];

// User tip for the UI
export const USER_SPEECH_TIP = 'Tip: Speak clearly and say the full place name, e.g., "Petaling Jaya".';

// Utility: Post-process transcript to auto-correct/suggest full place names
export function postProcessTranscript(transcript) {
  if (!transcript) return transcript;
  let processed = transcript;
  // Try to auto-complete partial place names
  LOCAL_PLACE_NAMES.forEach(place => {
    const parts = place.split(' ');
    // If last word of place is present but not the full name, suggest full name
    if (parts.length > 1) {
      const lastWord = parts[parts.length - 1].toLowerCase();
      if (processed.toLowerCase().includes(lastWord) && !processed.toLowerCase().includes(place.toLowerCase())) {
        // Replace the single word with the full place name
        const regex = new RegExp(`\\b${lastWord}\\b`, 'gi');
        processed = processed.replace(regex, place);
      }
    }
  });
  return processed;
}

let usingBrowserRecognizer = false;

const GoogleSpeechToText = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognizedLanguage, setRecognizedLanguage] = useState('en');
  const [error, setError] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [isWebView, setIsWebView] = useState(false);
  
  // Check environment on mount
  useEffect(() => {
    const webViewDetected = isAndroidWebView();
    setIsWebView(webViewDetected);
    console.log(`Environment detected: ${webViewDetected ? 'Android WebView' : 'Standard Browser'}`);
  }, []);
  
  // Initialize browser's SpeechRecognition
  useEffect(() => {
    // Try to initialize standard SpeechRecognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      try {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = false;
        
        // Set up event handlers
        recognitionInstance.onresult = (event) => {
          const speech = Array.from(event.results)
            .map((result) => result[0].transcript)
            .join(' ');
          // Post-process transcript for local place name correction
        setTranscript(postProcessTranscript(speech));
        };
        
        recognitionInstance.onend = () => {
          setIsListening(false);
        };
        
        recognitionInstance.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          setIsListening(false);
          setError(`Speech recognition error: ${event.error}`);
        };
        
        setRecognition(recognitionInstance);
        console.log('Standard SpeechRecognition initialized successfully');
      } catch (error) {
        console.error('Error initializing speech recognition:', error);
        setError('Failed to initialize speech recognition');
      }
    } else {
      console.log('SpeechRecognition API not available');
      setError('Speech recognition not supported in this browser');
    }
  }, []);
  
  // Request microphone access for WebView fallback
  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      console.log('Microphone access granted for WebView fallback');
      return stream;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setError('Microphone access denied or not available');
      return null;
    }
  };
  
  // Start listening for speech
  // Malay phrase hints for Google Cloud STT
  const malayPhraseHints = [
    'jo', 'pergi', 'Petaling Jaya', 'KL Sentral', 'tiket', 'stesen', 'kereta api', 'bas', 'kereta', 'teksi',
    'Kuala Lumpur', 'Subang', 'Seremban', 'Ipoh', 'Melaka', 'Shah Alam', 'Kajang', 'jalan', 'balik', 'naik',
    'turun', 'universiti', 'hospital', 'pasar', 'kedai', 'makan', 'minum', 'tempat', 'dekat', 'jauh', 'bandar',
    'pusat', 'selangor', 'pahang', 'penang', 'putrajaya', 'cyberjaya', 'ampang', 'rawang', 'gombak', 'klang',
    'nilai', 'bangi', 'bukit bintang', 'cheras', 'kepong', 'sentul', 'titiwangsa', 'wangsa maju', 'batu caves',
    'taman desa', 'bandar baru', 'seri kembangan', 'balakong', 'semenyih', 'semarak', 'kerinchi', 'pantai dalam',
    'mont kiara', 'lebuhraya', 'tol', 'plaza', 'datang', 'hantar', 'ambil', 'jemput', 'tunggu', 'sampai', 'berhenti',
    'mula', 'akhir', 'pukul', 'jam', 'hari', 'minggu', 'bulan', 'tahun', 'pagi', 'petang', 'malam', 'subuh', 'zohor',
    'asar', 'maghrib', 'isyak'
  ];
  const englishPhraseHints = [
    'Petaling Jaya', 'KL Sentral', 'Kuala Lumpur', 'Subang', 'Seremban', 'Ipoh', 'Melaka', 'Shah Alam', 'Kajang',
    'jalan', 'station', 'bus', 'car', 'taxi', 'ticket', 'go', 'come', 'grab', 'LRT', 'MRT', 'Komuter', 'Monorail',
    'university', 'hospital', 'market', 'shop', 'eat', 'drink', 'place', 'near', 'far', 'city', 'center', 'Selangor',
    'Pahang', 'Penang', 'Putrajaya', 'Cyberjaya', 'Ampang', 'Rawang', 'Gombak', 'Klang', 'Nilai', 'Bangi', 'Bukit Bintang',
    'Cheras', 'Kepong', 'Sentul', 'Titiwangsa', 'Wangsa Maju', 'Batu Caves', 'Taman Desa', 'Bandar Baru', 'Seri Kembangan',
    'Balakong', 'Semenyih', 'Semarak', 'Kerinchi', 'Pantai Dalam', 'Mont Kiara', 'highway', 'toll', 'plaza', 'send', 'pick up',
    'wait', 'arrive', 'stop', 'start', 'end', 'time', 'hour', 'minute', 'day', 'week', 'month', 'year', 'morning', 'afternoon',
    'evening', 'night', 'subuh', 'zohor', 'asar', 'maghrib', 'isyak'
  ];

  const startListening = useCallback(async (languageHint = 'en') => {
    setError(null);
    setTranscript('');
    
    // Standard browser approach
    if (recognition) {
      try {
        // Set language based on hint
        const langCode = SUPPORTED_LANGUAGES[languageHint]?.code || SUPPORTED_LANGUAGES.en.code;
        // Always ensure correct language code for browser recognizer
        recognition.lang = langCode;
        usingBrowserRecognizer = true; // Set flag for UI/logging
        // --- PHRASE HINTS FOR BACKEND GOOGLE CLOUD STT ---
        // If you are using a backend Google Cloud STT API, pass phrase hints for both Malay and English:
        // if (languageHint === 'ms') {
        //   sendToBackendWithPhraseHints(malayPhraseHints);
        //   console.log('[GoogleCloudSTT] Using Malay phrase hints:', malayPhraseHints);
        // }
        // if (languageHint === 'en') {
        //   sendToBackendWithPhraseHints(englishPhraseHints);
        //   console.log('[GoogleCloudSTT] Using English phrase hints:', englishPhraseHints);
        // }
        // For browser SpeechRecognition, phrase hints are not supported.
        // --- END PHRASE HINTS ---
        recognition.start();
        setIsListening(true);
        
        console.log(`[SpeechRecognition] Started with language: ${langCode} (hint: ${languageHint}) [USING BROWSER SPEECHRECOGNITION]`);
        return true;
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        // If standard approach fails, we'll try the WebView approach below
      }
    }
    // If we get here, we are NOT using Google Cloud STT.
    console.warn('[WARNING] Fallback to browser SpeechRecognition or WebView. Google Cloud STT is NOT being used. Accuracy may be reduced.');
    
    // WebView fallback approach - just get microphone access
    // In WebView, we'll just show a UI indicator that we're listening
    // but actual transcription will happen when stopListening is called
    try {
      const stream = await requestMicrophoneAccess();
      if (stream) {
        setIsListening(true);
        console.log('WebView fallback: Microphone activated');
        return true;
      }
    } catch (error) {
      console.error('WebView fallback error:', error);
    }
    
    setError('Failed to start speech recognition');
    setIsListening(false);
    return false;
  }, [recognition]);
  
  // Stop listening and detect language
  const stopListening = useCallback(async () => {
    // Standard browser approach
    if (recognition && isListening) {
      try {
        recognition.stop();
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
      }
    }
    
    // Stop any active media stream
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    
    setIsListening(false);
    
    // If we have a transcript from standard recognition
    if (transcript) {
      const detectedLanguage = await detectLanguage(transcript);
      setRecognizedLanguage(detectedLanguage);
      
      return {
        transcript,
        language: detectedLanguage
      };
    }
    
    // WebView fallback: If we're in WebView and don't have a transcript,
    // we can prompt the user to type their message instead
    if (isWebView) {
      // We'll handle this in the Chatbot component
      return { transcript: '', language: recognizedLanguage, webViewFallback: true };
    }
    
    return { transcript: '', language: recognizedLanguage };
  }, [recognition, isListening, transcript, recognizedLanguage, mediaStream, isWebView]);
  
  // Detect language using Google Cloud Translation API
  const detectLanguage = async (text) => {
    if (!text) return 'en';
    
    // Simple heuristic detection for short phrases
    // Chinese characters
    if (/[一-鿿]/.test(text)) {
      return 'zh';
    }

    // Urdu characters (Arabic script)
    if (/[؀-ۿ]/.test(text)) {
      return 'ur';
    }
    
    // Expanded Malay words and local expressions for better detection
    const malayWords = [
      'saya', 'anda', 'bagaimana', 'boleh', 'bantuan', 'perjalanan', 'destinasi', 'atau', 'dengan', 'untuk',
      'jo', 'pergi', 'ke', 'nak', 'sini', 'situ', 'jalan', 'stesen', 'bas', 'kereta', 'tiket',
      'kuala', 'lumpur', 'petaling', 'jaya', 'sentral', 'kl', 'subang', 'kajang', 'seremban', 'ipoh', 'melaka',
      'pulang', 'balik', 'naik', 'turun', 'keretapi', 'komuter', 'lrt', 'monorel', 'grab', 'teksi', 'motor',
      'jalan', 'sekolah', 'rumah', 'hospital', 'universiti', 'kampung', 'masjid', 'surau', 'pasar', 'kedai',
      'makan', 'minum', 'tempat', 'dekat', 'jauh', 'utara', 'selatan', 'timur', 'barat', 'bandar', 'pusat',
      'tengah', 'selangor', 'pahang', 'penang', 'pulau', 'pinang', 'langkawi', 'putrajaya', 'cyberjaya', 'ampang',
      'rawang', 'gombak', 'shah', 'alam', 'klang', 'nilai', 'bangi', 'setia', 'wangsa', 'bukit', 'bintang',
      'ampang', 'kerinchi', 'damansara', 'tropicana', 'sunway', 'usj', 'puchong', 'kinrara', 'cheras', 'kepong',
      'sentul', 'titiwangsa', 'ampang', 'melawati', 'wangsa', 'maju', 'gombak', 'selayang', 'batu', 'caves',
      'taman', 'desa', 'bandar', 'baru', 'putra', 'perdana', 'seri', 'kembangan', 'balakong', 'semenyih', 'kajang',
      'semarak', 'kerinchi', 'pantai', 'dalam', 'pantai', 'hillpark', 'sri', 'hartamas', 'mont', 'kiara',
      'jalan', 'raya', 'lebuhraya', 'tol', 'plaza', 'stesen', 'kereta', 'api', 'bas', 'teksi', 'grab', 'motor',
      'pergi', 'datang', 'hantar', 'ambil', 'jemput', 'tunggu', 'sampai', 'berhenti', 'mula', 'akhir', 'pukul', 'jam',
      'hari', 'minggu', 'bulan', 'tahun', 'pagi', 'petang', 'malam', 'tengahari', 'subuh', 'zohor', 'asar', 'maghrib', 'isyak'
    ];
    const words = text.toLowerCase().split(/\s+/);
    if (words.some(word => malayWords.includes(word))) {
      return 'ms';
    }
    
    // For longer text, use Google Cloud Translation API for language detection
    if (text.length > 10) {
      try {
        const API_KEY = process.env.REACT_APP_GOOGLE_TTS_API_KEY;
        const response = await fetch(`https://translation.googleapis.com/language/translate/v2/detect?key=${API_KEY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            q: text
          }),
        });
        
        const data = await response.json();
        
        if (data.data && data.data.detections && data.data.detections.length > 0) {
          const detection = data.data.detections[0][0];
          const langCode = detection.language;
          
          // Map to our supported languages
          if (langCode.startsWith('zh')) return 'zh';
          if (langCode === 'ms') return 'ms';
          return 'en'; // Default to English for other languages
        }
      } catch (error) {
        console.error('Error detecting language with Google API:', error);
      }
    }
    
    // Default to English if detection fails
    return 'en';
  };

  return {
    isListening,
    transcript,
    recognizedLanguage,
    error,
    startListening,
    stopListening,
    SUPPORTED_LANGUAGES,
    isWebView
  };
};

export default GoogleSpeechToText;
